import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Divider,
    IconButton,
    Chip
} from "@mui/material";

import dayjs from "dayjs";
import { toast } from "react-toastify";
import API from "../API/API";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const api = new API();

const tableHeaderStyle = {
    bgcolor: "#0b1120",
    color: "#00d7d7",
    fontSize: "11px",
    fontWeight: "bold",
    border: "1px solid #1c2b4d",
    textAlign: "center",
    whiteSpace: "nowrap",
    py: 1,
    px: 2,
    zIndex: 1
};

const cellStyle = {
    color: "#fff",
    fontSize: "11px",
    fontWeight: "500",
    border: "1px solid #1c2b4d",
    textAlign: "center",
    whiteSpace: "nowrap",
    py: 0.5
};

export default function RoomLedgerReport() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [selectedRow, setSelectedRow] = useState(null);

    const [historyData, setHistoryData] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`api/room-wise-ledgers?date=${selectedDate}`);
            const result = res?.data || {};
            setData(result.data || []);
            setSummary(result.summary || null);
        } catch (e) {
            toast.error("Failed to fetch ledger data");
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchHistory = async (id) => {
        try {
            const res = await api.get(`api/room-wise-ledgers/${id}/history`);
            setHistoryData(res?.data || null);
            setHistoryOpen(true);
        } catch (e) {
            toast.error("Failed to fetch history");
        }
    };

    const handleDirectSettle = async (row) => {
        if (!window.confirm(`Are you sure you want to settle Room ${row.room_number}?`)) return;
        setLoading(true);
        try {
            await api.post(`api/room-wise-ledgers/${row.id}/settle`);
            toast.success("Settlement successful");
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.message || "Settlement failed");
            setLoading(false);
        }
    };

    const handleToggleCredit = async (row) => {
        const isCredit = parseFloat(row.btc) > 0;
        const msg = isCredit ? "Cancel Credit and move to Balance?" : "Move balance to credit?";
        if (!window.confirm(msg)) return;
        try {
            await api.post(`api/room-wise-ledgers/toggle-credit-by-checkin/${row.check_in_id}`);
            toast.success(isCredit ? "Credit Cancelled" : "Moved to Credit Accounts");
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.message || (isCredit ? "Failed to cancel credit" : "Failed to move to credit"));
        }
    };

    const handlePrintRow = (row) => {
        const printWindow = window.open('', '_blank');
        const totalBal = parseFloat(row.total_balance !== undefined ? row.total_balance : (parseFloat(row.balance || 0) + parseFloat(row.addl_bal || 0))).toFixed(2);
        const content = `
      <html>
        <head>
          <title>Room Ledger - Room ${row.room_number}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #333; line-height: 1.6; }
            .header { text-align: center; border-bottom: 3px double #333; padding-bottom: 12px; margin-bottom: 25px; }
            .hotel-title { font-size: 24px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; margin: 0; color: #008080; }
            .report-title { font-size: 16px; color: #666; margin: 5px 0 0 0; font-weight: 500; }
            .info-table, .details-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            .info-table td { padding: 6px 10px; font-size: 14px; }
            .info-label { font-weight: bold; color: #555; width: 150px; }
            .info-value { color: #000; }
            .details-table th, .details-table td { border: 1px solid #ddd; padding: 10px; font-size: 13px; text-align: right; }
            .details-table th { background: #f5f5f5; font-weight: bold; color: #333; text-align: center; }
            .details-table td.text-left { text-align: left; }
            .details-table td.text-center { text-align: center; }
            .total-row { font-weight: bold; background-color: #fafafa; }
            .total-row td { border-top: 2px solid #333; }
            .highlight-due { color: #d32f2f; font-weight: bold; }
            .highlight-paid { color: #2e7d32; font-weight: bold; }
            .footer { margin-top: 50px; border-top: 1px solid #ddd; padding-top: 15px; font-size: 12px; text-align: center; color: #777; }
            @media print {
              body { padding: 10px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="hotel-title">PRESENT ROOM STATUS RECORD</div>
            <div class="report-title">Roomwise Ledger Statement</div>
          </div>
          
          <table class="info-table">
            <tr>
              <td class="info-label">Room Number:</td>
              <td class="info-value"><strong>${row.room_number}</strong> (${row.room_type || 'N/A'})</td>
              <td class="info-label">Bill Date:</td>
              <td class="info-value">${row.date || 'N/A'}</td>
            </tr>
            <tr>
              <td class="info-label">Guest Name:</td>
              <td class="info-value"><strong>${row.guest_name || 'N/A'}</strong></td>
              <td class="info-label">Company Name:</td>
              <td class="info-value">${row.company_name || 'N/A'}</td>
            </tr>
            <tr>
              <td class="info-label">Booking Type:</td>
              <td class="info-value">${row.booking_type || 'N/A'}</td>
              <td class="info-label">Food Plan:</td>
              <td class="info-value">${row.food_plan || 'N/A'}</td>
            </tr>
          </table>

          <table class="details-table">
            <thead>
              <tr>
                <th class="text-left">Description</th>
                <th>Charges (₹)</th>
                <th>Paid (₹)</th>
                <th>Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-left">Room Tariff & Services (Bill)</td>
                <td>₹${parseFloat(row.bill_amt || 0).toFixed(2)}</td>
                <td>₹${parseFloat(row.pay || 0).toFixed(2)}</td>
                <td class="highlight-due">₹${parseFloat(row.balance || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td class="text-left">Additional Services</td>
                <td>₹${parseFloat(row.addl_serv || 0).toFixed(2)}</td>
                <td>₹${parseFloat(row.addl_paid || 0).toFixed(2)}</td>
                <td class="highlight-due">₹${parseFloat(row.addl_bal || 0).toFixed(2)}</td>
              </tr>
              <tr class="total-row">
                <td class="text-left">Total Summary</td>
                <td>₹${parseFloat(row.total || 0).toFixed(2)}</td>
                <td class="highlight-paid">₹${(parseFloat(row.pay || 0) + parseFloat(row.addl_paid || 0)).toFixed(2)}</td>
                <td class="highlight-due">₹${totalBal}</td>
              </tr>
            </tbody>
          </table>

          <h3 style="font-size: 14px; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 30px;">Payment Breakdown</h3>
          <table class="info-table" style="margin-bottom: 10px;">
            <tr>
              <td class="info-label">Cash Paid:</td>
              <td class="info-value">₹${parseFloat(row.cash || 0).toFixed(2)}</td>
              <td class="info-label">UPI Paid:</td>
              <td class="info-value">₹${parseFloat(row.upi || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td class="info-label">Bank Paid:</td>
              <td class="info-value">₹${parseFloat(row.bank || 0).toFixed(2)}</td>
              <td class="info-label">Credit (BTC):</td>
              <td class="info-value">₹${parseFloat(row.btc || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td class="info-label">Refunded:</td>
              <td class="info-value">₹${parseFloat(row.refund || 0).toFixed(2)}</td>
              <td class="info-label"></td>
              <td class="info-value"></td>
            </tr>
          </table>

          <div class="footer">
            <p>This is an official computer-generated statement of account.</p>
            <p>Printed on: ${new Date().toLocaleString()}</p>
          </div>

          <script>
            window.onload = () => {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `;
        printWindow.document.write(content);
        printWindow.document.close();
    };


    return (
        <Box sx={{ bgcolor: "#0b1120", minHeight: "100vh", p: isMobile ? 1 : 2 }}>
            {/* Header Summary Bar */}
            <Box sx={{
                bgcolor: "#111c31", 
                p: 1.5, 
                mb: 2, 
                borderRadius: "8px", 
                border: "1px solid #1c2b4d",
                display: "flex", 
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center", 
                gap: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", width: isMobile ? "100%" : "auto" }}>
                    <IconButton onClick={() => window.history.back()} sx={{ color: "#00d7d7", mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ color: "#00d7d7", fontWeight: "bold", fontSize: isMobile ? "16px" : "18px" }}>
                        Present Room Status
                    </Typography>
                </Box>

                {(() => {
                    const totalBalanceSum = data.reduce((acc, row) => {
                        const tb = parseFloat(row.total_balance !== undefined ? row.total_balance : (parseFloat(row.balance || 0) + parseFloat(row.addl_bal || 0)));
                        return acc + tb;
                    }, 0);

                    return (
                        <Box sx={{ 
                            display: "flex", 
                            gap: isMobile ? 1 : 3, 
                            ml: isMobile ? 0 : "auto", 
                            flexWrap: "wrap",
                            justifyContent: "center"
                        }}>
                             <Box sx={{ textAlign: "center", minWidth: isMobile ? "60px" : "80px" }}>
                                <Typography sx={{ color: "#8faac8", fontSize: "10px" }}>CASH</Typography>
                                <Typography sx={{ color: "#00ff00", fontSize: "12px", fontWeight: "bold" }}>{summary?.cash_total || "0.00"}</Typography>
                            </Box>
                            <Box sx={{ textAlign: "center", minWidth: isMobile ? "60px" : "80px" }}>
                                <Typography sx={{ color: "#8faac8", fontSize: "10px" }}>UPI</Typography>
                                <Typography sx={{ color: "#4B90FC", fontSize: "12px", fontWeight: "bold" }}>{summary?.upi_total || "0.00"}</Typography>
                            </Box>
                            <Box sx={{ textAlign: "center", minWidth: isMobile ? "60px" : "80px" }}>
                                <Typography sx={{ color: "#8faac8", fontSize: "10px" }}>CREDIT</Typography>
                                <Typography sx={{ color: "#ffd700", fontSize: "12px", fontWeight: "bold" }}>{summary?.btc_total || "0.00"}</Typography>
                            </Box>
                            <Box sx={{ textAlign: "center", minWidth: isMobile ? "80px" : "100px" }}>
                                <Typography sx={{ color: "#8faac8", fontSize: "10px" }}>TOTAL BALANCE</Typography>
                                <Typography sx={{ color: "#ff4444", fontSize: "13px", fontWeight: "bold" }}>{totalBalanceSum.toFixed(2)}</Typography>
                            </Box>
                        </Box>
                    );
                })()}
            </Box>

            {/* Main Content Area */}
            <Box sx={{ border: "1px solid #1c2b4d", borderRadius: "8px", overflow: "hidden", bgcolor: "#111c31" }}>
                {!isMobile && (
                    <Box sx={{ py: 1.5, borderBottom: "1px solid #1c2b4d", textAlign: "center", bgcolor: "#000" }}>
                        <Typography sx={{ color: "#ffd700", fontWeight: "bold", fontSize: "16px", letterSpacing: "1px", textTransform: "uppercase" }}>
                            PRESENT ROOM STATUS REPORT
                        </Typography>
                    </Box>
                )}

                {isMobile ? (
                    /* Mobile Card View */
                    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress color="inherit" /></Box>
                        ) : data.length === 0 ? (
                            <Typography align="center" sx={{ color: '#8faac8', py: 5 }}>No rooms occupied</Typography>
                        ) : (
                            data.map((row, idx) => (
                                <Card key={row.id} variant="outlined" sx={{ bgcolor: '#0e1729', borderColor: '#253050', color: '#fff' }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography sx={{ color: '#00d7d7', fontWeight: 'bold', fontSize: '14px' }}>R.No: {row.room_number}</Typography>
                                            <Typography sx={{ color: '#8faac8', fontSize: '11px' }}>{row.date}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '16px', fontWeight: 'bold', mb: 0.5 }}>{row.guest_name}</Typography>
                                        <Typography sx={{ color: '#8faac8', fontSize: '11px', mb: 1 }}>{row.room_type} | {row.booking_type}</Typography>
                                        
                                        <Grid container spacing={1} sx={{ mt: 1 }}>
                                            <Grid item xs={6}>
                                                <Box sx={{ bgcolor: '#1a2035', p: 1, borderRadius: 1 }}>
                                                    <Typography sx={{ color: '#8faac8', fontSize: '9px' }}>BILL Amount</Typography>
                                                    <Typography sx={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>₹{parseFloat(row.bill_amt || 0).toFixed(2)}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ bgcolor: '#1a2035', p: 1, borderRadius: 1 }}>
                                                    <Typography sx={{ color: '#8faac8', fontSize: '9px' }}>BALANCE</Typography>
                                                    <Typography sx={{ color: '#ff4444', fontSize: '12px', fontWeight: 'bold' }}>₹{parseFloat(row.total_balance !== undefined ? row.total_balance : (parseFloat(row.balance || 0) + parseFloat(row.addl_bal || 0))).toFixed(2)}</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 1.5, bgcolor: '#253050' }} />
                                        
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            <Button 
                                                variant="contained" 
                                                size="small" 
                                                onClick={() => handleToggleCredit(row)}
                                                sx={{ 
                                                    bgcolor: parseFloat(row.btc) > 0 ? "#dc3545" : "#4B90FC", 
                                                    fontSize: '9px',
                                                    flexGrow: 1
                                                }}
                                            >
                                                {parseFloat(row.btc) > 0 ? "CANCEL CREDIT" : "TO CREDIT"}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={() => fetchHistory(row.id)}
                                                sx={{ borderColor: '#ffaa00', color: '#ffaa00', fontSize: '9px', flexGrow: 1 }}
                                            >
                                                HISTORY
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={() => handlePrintRow(row)}
                                                sx={{ borderColor: '#00d7d7', color: '#00d7d7', fontSize: '9px', flexGrow: 1 }}
                                            >
                                                PRINT
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </Box>
                ) : (
                    /* Desktop Table View */
                    <TableContainer sx={{ 
                        maxHeight: "calc(100vh - 250px)",
                        '&::-webkit-scrollbar': { height: '8px', width: '8px' },
                        '&::-webkit-scrollbar-track': { bgcolor: '#0e1729' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: '#3a4a6b', borderRadius: '4px' },
                        overflowX: "auto"
                    }}>
                        <Table size="small" stickyHeader sx={{ minWidth: "2500px" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderStyle} align="center">R-SHIFT</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="center">R.NO</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="center">BILL DATE</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="left">NAME</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">BILL</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">ADDL</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">ADDL.Paid</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">ADDL.Bal</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">TOTAL</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">PAY</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">BALANCE</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">TOTAL BALANCE</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">CREDIT</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">Refund</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="left">COMPANY</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="left">TYPE</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="left">BOOKING</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="left">PLAN</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">CASH</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">UPI</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="right">BANK</TableCell>
                                    <TableCell sx={tableHeaderStyle} align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={22} align="center" sx={{ py: 10 }}>
                                            <CircularProgress color="inherit" />
                                            <Typography sx={{ mt: 2, color: "#8faac8" }}>Loading Room Status...</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={22} align="center" sx={{ py: 5, color: "#8faac8" }}>No data available</TableCell>
                                    </TableRow>
                                ) : data.map((row, idx) => (
                                    <TableRow 
                                        key={row.id} 
                                        hover
                                        onClick={() => setSelectedRow(idx)}
                                        sx={{ 
                                            "&:hover": { bgcolor: "rgba(255, 0, 0, 0.15)" },
                                            cursor: 'pointer',
                                            bgcolor: selectedRow === idx ? 'rgba(255, 0, 0, 0.25) !important' : 'transparent'
                                        }}
                                    >
                                        <TableCell sx={{ ...cellStyle, textAlign: "center" }}>{row.shifted_from ? `${row.shifted_from} >>` : ""}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "center" }}>{row.room_number}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "center" }}>{row.date}</TableCell>
                                        <TableCell sx={{ ...cellStyle, fontWeight: "bold", textAlign: "left", minWidth: "150px" }}>{row.guest_name}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.bill_amt || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.addl_serv || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.addl_paid || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.addl_bal || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.total || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.pay || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.balance || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right", fontWeight: "bold" }}>
                                            {parseFloat(row.total_balance !== undefined ? row.total_balance : (parseFloat(row.balance || 0) + parseFloat(row.addl_bal || 0))).toFixed(2)}
                                        </TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.btc || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.refund || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "left" }}>{row.company_name}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "left" }}>{row.room_type}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "left" }}>{row.booking_type}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "left" }}>{row.food_plan}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.cash || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.upi || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{parseFloat(row.bank || 0).toFixed(2)}</TableCell>
                                        <TableCell sx={cellStyle}>
                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                                <Button variant="contained" size="small" onClick={() => handleToggleCredit(row)}
                                                    sx={{ bgcolor: parseFloat(row.btc) > 0 ? "#dc3545" : "#4B90FC", fontSize: "9px" }}>
                                                    {parseFloat(row.btc) > 0 ? "CANCEL CREDIT" : "CREDIT"}
                                                </Button>
                                                <Button variant="outlined" size="small" onClick={() => handleDirectSettle(row)}
                                                    sx={{ borderColor: "#ffaa00", color: "#ffaa00", fontSize: "9px" }}>
                                                    SETTLE
                                                </Button>
                                                <Button variant="outlined" size="small" onClick={() => fetchHistory(row.id)}
                                                    sx={{ borderColor: "#ffaa00", color: "#ffaa00", fontSize: "9px" }}>
                                                    HISTORY
                                                </Button>
                                                <Button variant="outlined" size="small" onClick={() => handlePrintRow(row)}
                                                    sx={{ borderColor: "#00d7d7", color: "#00d7d7", fontSize: "9px" }}>
                                                    PRINT
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* History Dialog */}
            <Dialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { bgcolor: "#0b1120", border: "2px solid #1c2b4d", color: "#fff" } }}
            >
                <DialogTitle sx={{ color: "#00d7d7", borderBottom: "1px solid #1c2b4d", display: 'flex', justifyContent: 'space-between' }}>
                    Booking History - Room {historyData?.ledger_summary?.room_number}
                    <Button onClick={() => setHistoryOpen(false)} sx={{ color: '#ff4444' }}>✕</Button>
                </DialogTitle>
                <DialogContent>
                    {historyData ? (
                        <Box sx={{ p: 1 }}>
                            <Box sx={{ bgcolor: "#111c31", p: 2, borderRadius: "6px", mb: 2 }}>
                                <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>{historyData.ledger_summary?.guest_name}</Typography>
                                <Typography sx={{ color: '#8faac8' }}>Booking ID: {historyData.ledger_summary?.booking_id}</Typography>
                            </Box>
                            
                            <Typography variant="subtitle1" sx={{ color: '#4B90FC', fontWeight: 'bold', mb: 1 }}>Room Advance History</Typography>
                            <TableContainer sx={{ mb: 3, border: '1px solid #1c2b4d' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#1a2035' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: '#00d7d7' }}>Date</TableCell>
                                            <TableCell sx={{ color: '#00d7d7' }}>Type</TableCell>
                                            <TableCell sx={{ color: '#00d7d7' }} align="right">Amount</TableCell>
                                            <TableCell sx={{ color: '#00d7d7' }}>Mode</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {historyData.room_advance_history?.map((h, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{ color: '#fff' }}>{dayjs(h.date).format("DD-MM-YYYY")}</TableCell>
                                                <TableCell sx={{ color: '#fff' }}>{h.payment_type}</TableCell>
                                                <TableCell sx={{ color: '#00ff00' }} align="right">₹{h.amount}</TableCell>
                                                <TableCell sx={{ color: '#fff' }}>{h.mode}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="subtitle1" sx={{ color: '#ffd700', fontWeight: 'bold', mb: 1 }}>Additional Services History</Typography>
                             <TableContainer sx={{ border: '1px solid #1c2b4d' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#1a2035' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: '#00d7d7' }}>Date</TableCell>
                                            <TableCell sx={{ color: '#00d7d7' }}>Service</TableCell>
                                            <TableCell sx={{ color: '#00d7d7' }} align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {historyData.additional_services_history?.map((h, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{ color: '#fff' }}>{dayjs(h.date).format("DD-MM-YYYY")}</TableCell>
                                                <TableCell sx={{ color: '#fff' }}>{h.payment_type}</TableCell>
                                                <TableCell sx={{ color: '#00ff00' }} align="right">₹{h.amount}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : <CircularProgress color="inherit" />}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
